import React, { useEffect, useMemo, useState } from "react";
import { LogIn, Search, Send, ShieldAlert, Inbox, Download, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_FILTERS = [
  { id: "all", label: "All" },
  { id: "chat", label: "Chat" },
  { id: "download", label: "Downloads" },
  { id: "quote", label: "Quotes" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? iso.slice(0, 16) : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function Leads() {
  const { user, loading, login } = useAuth();
  const [state, setState] = useState("checking"); // checking | forbidden | ready
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [sending, setSending] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { setState("checking"); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/admin/leads`, { credentials: "include" });
        if (res.status === 403) { setState("forbidden"); return; }
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        setLeads(data.leads || []);
        setState("ready");
      } catch {
        setState("forbidden");
      }
    })();
  }, [user, loading]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (type !== "all" && l.type !== type) return false;
      if (!needle) return true;
      return [l.name, l.email, l.org, l.message, l.page].some((v) => (v || "").toLowerCase().includes(needle));
    });
  }, [leads, q, type]);

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/leads/digest`, { method: "POST", credentials: "include" });
      const data = await res.json();
      toast.success(data.sent ? `Digest sent — ${data.sent} lead${data.sent === 1 ? "" : "s"} emailed to you.` : "No new leads to send right now.");
    } catch {
      toast.error("Couldn't send the digest. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const updateLead = async (id, patch) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      const res = await fetch(`${API}/admin/leads/${id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("failed");
    } catch {
      toast.error("Couldn't save that change. Please try again.");
    }
  };

  const exportCsv = () => {
    const cols = ["created_at", "type", "status", "name", "email", "org", "page", "message", "note"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(","), ...filtered.map((l) => cols.map((c) => esc(l[c])).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"} to CSV.`);
  };

  return (
    <div className="grain min-h-screen" data-testid="leads-page">
      <Nav />
      <section className="pt-32 md:pt-40 pb-20 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c mb-3">Private · For Casey</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight mb-8">Leads</h1>

          {(loading || (!user && state === "checking")) && !user && (
            <div className="glass shape-r p-10 text-center max-w-md" data-testid="leads-signin">
              <LogIn className="mx-auto mb-4 text-primary-c" size={28} />
              <h2 className="font-serif text-2xl mb-2">Sign in to view leads</h2>
              <p className="text-fg/60 text-sm mb-6">This area is private. Sign in with the account Casey uses.</p>
              <button
                onClick={login}
                data-testid="leads-signin-btn"
                className="inline-flex items-center gap-2 shape-r bg-primary-c text-bg font-medium px-6 py-3 hover:opacity-90 transition-opacity"
              >
                <LogIn size={16} /> Sign in with Google
              </button>
            </div>
          )}

          {user && state === "forbidden" && (
            <div className="glass shape-r p-10 text-center max-w-md" data-testid="leads-forbidden">
              <ShieldAlert className="mx-auto mb-4 text-primary-c" size={28} />
              <h2 className="font-serif text-2xl mb-2">This area is for Casey.</h2>
              <p className="text-fg/60 text-sm">You're signed in as {user.email}, which doesn't have access to the leads dashboard.</p>
            </div>
          )}

          {user && state === "ready" && (
            <div data-testid="leads-dashboard">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg/40" />
                  <input
                    data-testid="leads-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, email, org, message…"
                    className="w-full shape-r bg-black/25 border border-white/15 focus:outline-none focus:border-primary-c pl-10 pr-3 py-3 text-sm text-fg placeholder:text-fg/30 transition-colors"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {TYPE_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`leads-filter-${f.id}`}
                      onClick={() => setType(f.id)}
                      className="px-3.5 py-2 rounded-full text-xs font-medium border transition-colors"
                      style={{ borderColor: type === f.id ? "var(--primary)" : "rgba(255,255,255,0.15)", backgroundColor: type === f.id ? "var(--primary)" : "transparent", color: type === f.id ? "var(--bg)" : "var(--fg)" }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={exportCsv}
                  data-testid="leads-export-csv"
                  className="inline-flex items-center gap-2 shape-r border border-white/20 text-fg/80 px-4 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  <Download size={15} /> Export CSV
                </button>
                <button
                  onClick={sendDigest}
                  disabled={sending}
                  data-testid="leads-send-digest"
                  className="inline-flex items-center gap-2 shape-r border border-primary-c/50 text-primary-c px-4 py-2.5 text-sm font-medium hover:bg-primary-c/10 transition-colors disabled:opacity-50"
                >
                  <Send size={15} /> {sending ? "Sending…" : "Email me a digest"}
                </button>
              </div>

              <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-fg/45 mb-4" data-testid="leads-count">
                {filtered.length} {filtered.length === 1 ? "lead" : "leads"}{q || type !== "all" ? ` · filtered from ${leads.length}` : ""}
              </p>

              {filtered.length === 0 ? (
                <div className="glass shape-r p-12 text-center text-fg/50" data-testid="leads-empty">
                  <Inbox className="mx-auto mb-3" size={26} />
                  No leads match your search yet.
                </div>
              ) : (
                <div className="glass shape-r overflow-hidden" data-testid="leads-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left font-mono text-[10px] tracking-[0.15em] uppercase text-fg/45 border-b border-white/10">
                          <th className="px-4 py-3 font-normal">When</th>
                          <th className="px-4 py-3 font-normal">Type</th>
                          <th className="px-4 py-3 font-normal">Status</th>
                          <th className="px-4 py-3 font-normal">Name</th>
                          <th className="px-4 py-3 font-normal">Email</th>
                          <th className="px-4 py-3 font-normal">Org</th>
                          <th className="px-4 py-3 font-normal">Page</th>
                          <th className="px-4 py-3 font-normal">Message</th>
                          <th className="px-4 py-3 font-normal">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((l, i) => {
                          const status = l.status || "new";
                          const contacted = status === "contacted";
                          return (
                          <tr key={l.id || i} data-testid={`leads-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors align-top">
                            <td className="px-4 py-3 whitespace-nowrap text-fg/55">{fmtDate(l.created_at)}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider" style={{ backgroundColor: "var(--primary)", color: "var(--bg)" }}>{l.type}</span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => updateLead(l.id, { status: contacted ? "new" : "contacted" })}
                                data-testid={`leads-status-${i}`}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${contacted ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10" : "border-white/20 text-fg/55 hover:border-white/40"}`}
                                title={contacted ? "Mark as new" : "Mark as contacted"}
                              >
                                {contacted ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                                {contacted ? "Contacted" : "New"}
                              </button>
                            </td>
                            <td className="px-4 py-3 font-medium whitespace-nowrap">{l.name}</td>
                            <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-primary-c hover:underline">{l.email}</a></td>
                            <td className="px-4 py-3 text-fg/60">{l.org || "—"}</td>
                            <td className="px-4 py-3 text-fg/60 whitespace-nowrap">{l.page}</td>
                            <td className="px-4 py-3 text-fg/70 max-w-xs">{l.message || "—"}</td>
                            <td className="px-4 py-3">
                              <input
                                data-testid={`leads-note-${i}`}
                                defaultValue={l.note || ""}
                                onBlur={(e) => { if ((e.target.value || "") !== (l.note || "")) updateLead(l.id, { note: e.target.value }); }}
                                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                placeholder="Add a note…"
                                className="w-40 shape-r bg-black/25 border border-white/15 focus:outline-none focus:border-primary-c px-2.5 py-1.5 text-xs text-fg placeholder:text-fg/30 transition-colors"
                              />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
